import React, { useState } from 'react';
import {
  Box, Card, CardContent, Button, Stack, Typography, Alert, Divider, TextField,
  Table, TableBody, TableRow, TableCell, CircularProgress
} from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKey';
import VerifiedIcon from '@mui/icons-material/Verified';
import PageHeader from '../components/PageHeader';
import JsonViewer from '../components/JsonViewer';
import TokenField from '../components/TokenField';
import api from '../api';

const ClaimsTable = ({ claims }) => (
  <Table size="small">
    <TableBody>
      {Object.entries(claims).map(([k, v]) => (
        <TableRow key={k}>
          <TableCell sx={{ fontWeight: 600, width: 160, verticalAlign: 'top' }}>{k}</TableCell>
          <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const M2MTest = () => {
  // Optional creds to test ANOTHER caller (blank => this product's own client).
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  // Paste-a-token verify (no credentials needed).
  const [pasteToken, setPasteToken] = useState('');
  const [pasteResult, setPasteResult] = useState(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingPaste, setLoadingPaste] = useState(false);
  const [error, setError] = useState(null);

  const requestToken = async () => {
    setLoadingToken(true); setError(null); setVerifyResult(null);
    try {
      const creds =
        clientId.trim() && clientSecret.trim()
          ? { clientId: clientId.trim(), clientSecret: clientSecret.trim() }
          : {};
      const { data } = await api.post('/m2m/token', creds);
      setTokenInfo(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoadingToken(false);
    }
  };

  const verify = async () => {
    setLoadingVerify(true); setError(null);
    try {
      const { data } = await api.post('/m2m/verify', tokenInfo?.token ? { token: tokenInfo.token } : {});
      setVerifyResult(data);
    } catch (err) {
      setVerifyResult(err.response?.data || { valid: false, message: err.message });
    } finally {
      setLoadingVerify(false);
    }
  };

  const verifyPasted = async () => {
    setLoadingPaste(true); setPasteResult(null);
    try {
      const { data } = await api.post('/m2m/verify', { token: pasteToken.trim() });
      setPasteResult(data);
    } catch (err) {
      setPasteResult(err.response?.data || { valid: false, message: err.message });
    } finally {
      setLoadingPaste(false);
    }
  };

  const usedClient = tokenInfo?.claims?.sub;

  return (
    <Box>
      <PageHeader
        title="M2M Token Test"
        subtitle="Exercise the client_credentials flow — for this product's client or any other caller — and verify the token against the discovery /jwks."
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<KeyIcon />} onClick={requestToken} disabled={loadingToken}>
              Request token
            </Button>
            <Button variant="outlined" startIcon={<VerifiedIcon />} onClick={verify} disabled={loadingVerify || !tokenInfo}>
              Verify
            </Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Caller credentials (optional)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test another caller's M2M client by entering its <code>client_id</code> and{' '}
            <code>client_secret</code> (e.g. <code>pluriell</code>, <code>test-caller</code>,{' '}
            <code>messaging-common</code>). Leave both blank to use this product's own client.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="client_id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              fullWidth
              size="small"
              placeholder="blank = this product's client"
            />
            <TextField
              label="client_secret"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              fullWidth
              size="small"
              type="password"
            />
          </Stack>
        </CardContent>
      </Card>

      {verifyResult && (
        <Alert severity={verifyResult.valid ? 'success' : 'error'} sx={{ mb: 2 }}>
          {verifyResult.valid ? 'Token verified against the discovery JWKS (/jwks).' : `Verification failed: ${verifyResult.message}`}
        </Alert>
      )}

      {loadingToken ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : tokenInfo ? (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography variant="h6">Token</Typography>
                {usedClient && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    client: {usedClient}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  expires {tokenInfo.expiresAt ? new Date(tokenInfo.expiresAt).toLocaleTimeString() : '—'}
                </Typography>
              </Stack>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is the full, usable M2M token{usedClient ? ` (${usedClient})` : ''} — shown because this is a testing product. Treat it like a password.
              </Alert>
              <Box sx={{ mb: 2 }}>
                <TokenField
                  label={`M2M token${usedClient ? ` (${usedClient})` : ''}`}
                  value={tokenInfo.token}
                  caption="client_credentials JWT. Verifiable against the gateway discovery /jwks."
                />
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="overline" color="text.secondary">Decoded claims</Typography>
              <ClaimsTable claims={tokenInfo.claims} />
            </CardContent>
          </Card>

          {verifyResult?.claims && <JsonViewer title="Verified claims (from JWKS)" data={verifyResult.claims} />}
        </Stack>
      ) : (
        <Alert severity="info">Click <strong>Request token</strong> to obtain an M2M token.</Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Verify a token</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste any Milient M2M token to verify it against the gateway discovery /jwks — no credentials needed.
          </Typography>
          <TextField
            label="M2M token"
            value={pasteToken}
            onChange={(e) => setPasteToken(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2, '& textarea': { fontFamily: 'monospace' } }}
          />
          <Button
            variant="outlined"
            startIcon={<VerifiedIcon />}
            onClick={verifyPasted}
            disabled={loadingPaste || !pasteToken.trim()}
          >
            Verify token
          </Button>
          {pasteResult && (
            <Alert severity={pasteResult.valid ? 'success' : 'error'} sx={{ mt: 2 }}>
              {pasteResult.valid ? 'Token verified against the discovery JWKS (/jwks).' : `Verification failed: ${pasteResult.message}`}
            </Alert>
          )}
          {pasteResult?.claims && (
            <Box sx={{ mt: 2 }}>
              <JsonViewer title="Verified claims (from JWKS)" data={pasteResult.claims} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default M2MTest;
